import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Chip, Tooltip as MuiTooltip } from '@mui/material';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  CandlestickData,
  LineData,
  HistogramData,
  CrosshairMode,
  LineStyle,
  UTCTimestamp,
  LogicalRange,
} from 'lightweight-charts';
import { bloomberg } from '../theme/bloombergTheme';
import { useThemeMode } from '../theme/ThemeContext';
import { TechnicalChartPoint, FibonacciLevels } from '../services/api';

// ──────────────────────────────────────────────────────────────────────────
// Professional charting component built on TradingView's lightweight-charts.
//
// Renders:
//   • Main pane  — candlesticks (or line/area) + volume histogram + MA/BB overlays
//   • RSI pane   — RSI line with 30/70 guides (toggle)
//   • MACD pane  — MACD/signal lines + histogram (toggle)
//   • Crosshair with a floating OHLC legend
//   • Fibonacci + support/resistance price lines (toggle)
//
// The three panes are separate chart instances with their time scales kept in
// sync — the classic, robust multi-pane pattern that works across versions.
// ──────────────────────────────────────────────────────────────────────────

export type ChartType = 'candles' | 'line' | 'area';

interface ProChartProps {
  data: TechnicalChartPoint[];
  /** engine-detected structure (S/R, trendlines, channels, patterns) */
  annotations?: import('../services/api').ChartAnnotations | null;
  fibonacci?: FibonacciLevels | null;
  periodHigh?: number;
  periodLow?: number;
  currencySymbol?: string;
  height?: number;
}

// Indicator toggle state
interface Overlays {
  ma20: boolean;
  ma50: boolean;
  ma200: boolean;
  bb: boolean;
  volume: boolean;
  rsi: boolean;
  macd: boolean;
  fib: boolean;
  sr: boolean;
  auto: boolean;
}

const DEFAULT_OVERLAYS: Overlays = {
  ma20: true, ma50: true, ma200: false, bb: false,
  volume: true, rsi: false, macd: false, fib: false, sr: false, auto: true,
};

const COLORS = {
  up: '#22c55e',
  down: '#ef4444',
  ma20: '#ff8c00',
  ma50: '#3b82f6',
  ma200: '#a855f7',
  bb: '#64748b',
  rsi: '#22d3ee',
  macd: '#3b82f6',
  macdSignal: '#f59e0b',
  fib: '#eab308',
  resistance: '#ef4444',
  support: '#22c55e',
};

// Convert "YYYY-MM-DD" or ISO datetime to a lightweight-charts Time.
function toTime(dateStr: string): UTCTimestamp {
  // Use UTC timestamp (seconds) so intraday + daily both work
  const ms = new Date(dateStr.length <= 10 ? `${dateStr}T00:00:00Z` : dateStr).getTime();
  return Math.floor(ms / 1000) as UTCTimestamp;
}

const ProChart: React.FC<ProChartProps> = ({
  data,
  annotations,
  fibonacci,
  periodHigh,
  periodLow,
  currencySymbol = '$',
  height = 460,
}) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const mainRef = useRef<HTMLDivElement>(null);
  const rsiRef = useRef<HTMLDivElement>(null);
  const macdRef = useRef<HTMLDivElement>(null);

  const mainChart = useRef<IChartApi | null>(null);
  const rsiChart = useRef<IChartApi | null>(null);
  const macdChart = useRef<IChartApi | null>(null);

  const priceSeries = useRef<ISeriesApi<'Candlestick'> | ISeriesApi<'Line'> | ISeriesApi<'Area'> | null>(null);
  const volSeries = useRef<ISeriesApi<'Histogram'> | null>(null);
  const ma20Series = useRef<ISeriesApi<'Line'> | null>(null);
  const ma50Series = useRef<ISeriesApi<'Line'> | null>(null);
  const ma200Series = useRef<ISeriesApi<'Line'> | null>(null);
  const bbU = useRef<ISeriesApi<'Line'> | null>(null);
  const bbM = useRef<ISeriesApi<'Line'> | null>(null);
  const bbL = useRef<ISeriesApi<'Line'> | null>(null);

  const [chartType, setChartType] = useState<ChartType>('candles');
  const [logScale, setLogScale] = useState(false);
  const [overlays, setOverlays] = useState<Overlays>(DEFAULT_OVERLAYS);
  const [legend, setLegend] = useState<{
    o?: number; h?: number; l?: number; c?: number; v?: number; chg?: number; date?: string;
  } | null>(null);

  const theme = useMemo(() => ({
    bg: isDark ? '#0d0f14' : '#ffffff',
    text: isDark ? '#94a3b8' : '#475569',
    grid: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(71,85,105,0.10)',
    border: isDark ? '#1e293b' : '#e2e8f0',
    crosshair: isDark ? '#64748b' : '#94a3b8',
  }), [isDark]);

  // ── Build / rebuild charts when data or theme changes ──
  useEffect(() => {
    if (!mainRef.current || !data || data.length === 0) return;

    const common = {
      layout: {
        background: { color: 'transparent' },
        textColor: theme.text,
        fontSize: 11,
        fontFamily: 'IBM Plex Mono, monospace',
      },
      grid: {
        vertLines: { color: theme.grid },
        horzLines: { color: theme.grid },
      },
      timeScale: {
        borderColor: theme.border,
        timeVisible: true,
        secondsVisible: false,
      },
      rightPriceScale: { borderColor: theme.border },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: theme.crosshair, width: 1 as const, style: LineStyle.Dashed, labelBackgroundColor: '#ff8c00' },
        horzLine: { color: theme.crosshair, width: 1 as const, style: LineStyle.Dashed, labelBackgroundColor: '#ff8c00' },
      },
    };

    // ── Main price chart ──
    const mc = createChart(mainRef.current, {
      ...common,
      width: mainRef.current.clientWidth,
      height: overlays.rsi || overlays.macd ? height * 0.62 : height,
      rightPriceScale: { ...common.rightPriceScale, mode: logScale ? 1 : 0, scaleMargins: { top: 0.08, bottom: overlays.volume ? 0.26 : 0.08 } },
    });
    mainChart.current = mc;

    // Price series
    if (chartType === 'candles') {
      const s = mc.addCandlestickSeries({
        upColor: COLORS.up, downColor: COLORS.down,
        borderUpColor: COLORS.up, borderDownColor: COLORS.down,
        wickUpColor: COLORS.up, wickDownColor: COLORS.down,
        priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
      });
      s.setData(data
        .filter(d => d.open != null && d.high != null && d.low != null && d.price != null)
        .map(d => ({
          time: toTime(d.date), open: d.open, high: d.high, low: d.low, close: d.price,
        } as CandlestickData)));
      priceSeries.current = s;
    } else {
      const lineData = data
        .filter(d => d.price != null)
        .map(d => ({ time: toTime(d.date), value: d.price } as LineData));
      if (chartType === 'area') {
        const s = mc.addAreaSeries({
          lineColor: '#ff8c00', topColor: 'rgba(255,140,0,0.28)', bottomColor: 'rgba(255,140,0,0.02)',
          lineWidth: 2, priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
        });
        s.setData(lineData); priceSeries.current = s;
      } else {
        const s = mc.addLineSeries({ color: '#ff8c00', lineWidth: 2, priceFormat: { type: 'price', precision: 2, minMove: 0.01 } });
        s.setData(lineData); priceSeries.current = s;
      }
    }

    // Volume histogram (overlaid, own invisible scale)
    if (overlays.volume) {
      const vs = mc.addHistogramSeries({
        priceFormat: { type: 'volume' },
        priceScaleId: 'vol',
        color: theme.grid,
      });
      mc.priceScale('vol').applyOptions({ scaleMargins: { top: 0.78, bottom: 0 } });
      vs.setData(data.filter(d => d.volume != null).map(d => ({
        time: toTime(d.date),
        value: d.volume,
        color: (d.price ?? 0) >= (d.open ?? d.price ?? 0) ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)',
      } as HistogramData)));
      volSeries.current = vs;
    }

    // Moving-average overlays
    const addMA = (key: 'ma_20' | 'ma_50' | 'ma_200', color: string, ref: React.MutableRefObject<ISeriesApi<'Line'> | null>) => {
      const s = mc.addLineSeries({ color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
      s.setData(data.filter(d => d[key] != null).map(d => ({ time: toTime(d.date), value: d[key] as number } as LineData)));
      ref.current = s;
    };
    if (overlays.ma20) addMA('ma_20', COLORS.ma20, ma20Series);
    if (overlays.ma50) addMA('ma_50', COLORS.ma50, ma50Series);
    if (overlays.ma200) addMA('ma_200', COLORS.ma200, ma200Series);

    // Bollinger Bands
    if (overlays.bb) {
      const mk = (key: 'bb_upper' | 'bb_middle' | 'bb_lower', dashed: boolean) => {
        const s = mc.addLineSeries({
          color: COLORS.bb, lineWidth: 1, lineStyle: dashed ? LineStyle.Dotted : LineStyle.Solid,
          priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        s.setData(data.filter(d => d[key] != null).map(d => ({ time: toTime(d.date), value: d[key] as number } as LineData)));
        return s;
      };
      bbU.current = mk('bb_upper', true);
      bbM.current = mk('bb_middle', false);
      bbL.current = mk('bb_lower', true);
    }

    // Fibonacci price lines (drawn on the price series)
    if (overlays.fib && fibonacci && priceSeries.current) {
      const fibs: { v: number; label: string }[] = [
        { v: fibonacci.fib_0, label: '0%' },
        { v: fibonacci.fib_236, label: '23.6%' },
        { v: fibonacci.fib_382, label: '38.2%' },
        { v: fibonacci.fib_500, label: '50%' },
        { v: fibonacci.fib_618, label: '61.8%' },
        { v: fibonacci.fib_786, label: '78.6%' },
        { v: fibonacci.fib_100, label: '100%' },
      ];
      fibs.forEach(f => {
        if (f.v == null) return;
        priceSeries.current!.createPriceLine({
          price: f.v, color: COLORS.fib, lineWidth: 1, lineStyle: LineStyle.Dashed,
          axisLabelVisible: true, title: `Fib ${f.label}`,
        });
      });
    }

    // Support / Resistance (period high/low) price lines
    if (overlays.sr && priceSeries.current) {
      if (periodHigh != null) priceSeries.current.createPriceLine({
        price: periodHigh, color: COLORS.resistance, lineWidth: 1, lineStyle: LineStyle.Dashed,
        axisLabelVisible: true, title: 'Period High',
      });
      if (periodLow != null) priceSeries.current.createPriceLine({
        price: periodLow, color: COLORS.support, lineWidth: 1, lineStyle: LineStyle.Dashed,
        axisLabelVisible: true, title: 'Period Low',
      });
    }

    // ── AUTO annotations: engine-detected levels, trendlines, channels, patterns ──
    if (overlays.auto && annotations && priceSeries.current) {
      (annotations.levels || []).forEach(l => {
        priceSeries.current!.createPriceLine({
          price: l.price,
          color: l.kind === 'resistance' ? COLORS.resistance : COLORS.support,
          lineWidth: l.major ? 2 : 1,
          lineStyle: l.major ? LineStyle.Solid : LineStyle.Dashed,
          axisLabelVisible: true,
          title: `${l.major ? 'MAJOR ' : ''}${l.kind === 'resistance' ? 'R' : 'S'}×${l.touches}`,
        });
      });
      const drawAnnoLine = (pts: { date: string; price: number }[], color: string) => {
        if (!pts || pts.length < 2) return;
        const ls = mc.addLineSeries({
          color, lineWidth: 2, lineStyle: LineStyle.Dashed,
          priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        ls.setData(pts.map(pt => ({ time: toTime(pt.date), value: pt.price })));
      };
      (annotations.trendlines || []).forEach(t =>
        drawAnnoLine(t.points, t.kind === 'resistance' ? COLORS.resistance : COLORS.support));
      (annotations.channels || []).forEach(ch => {
        drawAnnoLine(ch.upper, '#60a5fa');
        drawAnnoLine(ch.lower, '#60a5fa');
      });
      (annotations.patterns || []).forEach(pat =>
        ((pat.geometry && pat.geometry.lines) || []).forEach(l =>
          drawAnnoLine(l.points, pat.direction === 'bullish' ? COLORS.up : COLORS.down)));
    }

    // ── RSI pane ──
    if (overlays.rsi && rsiRef.current) {
      const rc = createChart(rsiRef.current, {
        ...common, width: rsiRef.current.clientWidth, height: height * 0.19,
        rightPriceScale: { ...common.rightPriceScale, scaleMargins: { top: 0.1, bottom: 0.1 } },
      });
      rsiChart.current = rc;
      const rs = rc.addLineSeries({ color: COLORS.rsi, lineWidth: 1, priceFormat: { type: 'price', precision: 1, minMove: 0.1 } });
      rs.setData(data.filter(d => d.rsi != null).map(d => ({ time: toTime(d.date), value: d.rsi as number } as LineData)));
      [30, 50, 70].forEach(lvl => rs.createPriceLine({
        price: lvl, color: lvl === 50 ? theme.grid : theme.crosshair,
        lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `${lvl}`,
      }));
    }

    // ── MACD pane ──
    if (overlays.macd && macdRef.current) {
      const xc = createChart(macdRef.current, {
        ...common, width: macdRef.current.clientWidth, height: height * 0.19,
        rightPriceScale: { ...common.rightPriceScale, scaleMargins: { top: 0.15, bottom: 0.1 } },
      });
      macdChart.current = xc;
      const hist = xc.addHistogramSeries({ priceFormat: { type: 'price', precision: 3, minMove: 0.001 } });
      hist.setData(data.filter(d => d.macd_histogram != null).map(d => ({
        time: toTime(d.date), value: d.macd_histogram as number,
        color: (d.macd_histogram as number) >= 0 ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)',
      } as HistogramData)));
      const ml = xc.addLineSeries({ color: COLORS.macd, lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
      ml.setData(data.filter(d => d.macd != null).map(d => ({ time: toTime(d.date), value: d.macd as number } as LineData)));
      const sl = xc.addLineSeries({ color: COLORS.macdSignal, lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
      sl.setData(data.filter(d => d.macd_signal != null).map(d => ({ time: toTime(d.date), value: d.macd_signal as number } as LineData)));
    }

    // ── Sync time scales across panes ──
    const charts = [mc, rsiChart.current, macdChart.current].filter(Boolean) as IChartApi[];
    const syncing = { active: false };
    const syncRange = (src: IChartApi) => (range: LogicalRange | null) => {
      if (syncing.active || !range) return;
      syncing.active = true;
      charts.forEach(c => { if (c !== src) c.timeScale().setVisibleLogicalRange(range); });
      syncing.active = false;
    };
    charts.forEach(c => c.timeScale().subscribeVisibleLogicalRangeChange(syncRange(c)));

    // ── Crosshair legend (OHLCV readout) ──
    const onMove = (param: any) => {
      if (!param.time || !priceSeries.current) { return; }
      const idx = data.findIndex(d => toTime(d.date) === param.time);
      if (idx < 0) return;
      const d = data[idx];
      const prev = idx > 0 ? data[idx - 1].price : d.open;
      setLegend({
        o: d.open, h: d.high, l: d.low, c: d.price, v: d.volume,
        chg: prev ? ((d.price - prev) / prev) * 100 : 0,
        date: d.date.slice(0, 10),
      });
    };
    mc.subscribeCrosshairMove(onMove);

    mc.timeScale().fitContent();

    // ── Resize handling ──
    const ro = new ResizeObserver(() => {
      if (mainRef.current) mc.applyOptions({ width: mainRef.current.clientWidth });
      if (rsiRef.current && rsiChart.current) rsiChart.current.applyOptions({ width: rsiRef.current.clientWidth });
      if (macdRef.current && macdChart.current) macdChart.current.applyOptions({ width: macdRef.current.clientWidth });
    });
    if (mainRef.current) ro.observe(mainRef.current);

    return () => {
      ro.disconnect();
      mc.remove();
      if (rsiChart.current) { rsiChart.current.remove(); rsiChart.current = null; }
      if (macdChart.current) { macdChart.current.remove(); macdChart.current = null; }
      mainChart.current = null;
      priceSeries.current = null;
    };
  }, [data, chartType, logScale, overlays, theme, height, fibonacci, periodHigh, periodLow, annotations]);

  const toggle = (key: keyof Overlays) => setOverlays(o => ({ ...o, [key]: !o[key] }));

  // Default legend = last point with a valid price (skip synthetic null tail)
  const lastValidIdx = useMemo(() => {
    if (!data) return -1;
    for (let i = data.length - 1; i >= 0; i--) if (data[i]?.price != null) return i;
    return -1;
  }, [data]);
  const last = lastValidIdx >= 0 ? data[lastValidIdx] : null;
  const prevPrice = lastValidIdx > 0 ? data[lastValidIdx - 1]?.price : undefined;
  const live = legend || (last ? {
    o: last.open, h: last.high, l: last.low, c: last.price, v: last.volume,
    chg: prevPrice ? ((last.price - prevPrice) / prevPrice) * 100 : 0,
    date: last.date.slice(0, 10),
  } : null);

  const fmt = (v?: number) => v == null ? '—' : `${currencySymbol}${v.toFixed(2)}`;
  const fmtVol = (v?: number) => v == null ? '—' : v >= 1e9 ? `${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : `${v}`;
  const chgColor = live && live.chg != null ? (live.chg >= 0 ? COLORS.up : COLORS.down) : bloomberg.text.muted;

  const indicatorChips: { key: keyof Overlays; label: string; color: string }[] = [
    { key: 'auto', label: 'AUTO', color: '#ff8c00' },
    { key: 'ma20', label: 'MA20', color: COLORS.ma20 },
    { key: 'ma50', label: 'MA50', color: COLORS.ma50 },
    { key: 'ma200', label: 'MA200', color: COLORS.ma200 },
    { key: 'bb', label: 'BB', color: COLORS.bb },
    { key: 'volume', label: 'VOL', color: '#64748b' },
    { key: 'rsi', label: 'RSI', color: COLORS.rsi },
    { key: 'macd', label: 'MACD', color: COLORS.macd },
    { key: 'fib', label: 'FIB', color: COLORS.fib },
    { key: 'sr', label: 'S/R', color: '#94a3b8' },
  ];

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 1.25, mb: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={chartType} exclusive size="small"
          onChange={(_, v) => v && setChartType(v)}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: '0.7rem' } }}
        >
          <ToggleButton value="candles">Candles</ToggleButton>
          <ToggleButton value="line">Line</ToggleButton>
          <ToggleButton value="area">Area</ToggleButton>
        </ToggleButtonGroup>

        <MuiTooltip title="Toggle logarithmic price scale" arrow>
          <Chip
            label="LOG" size="small" clickable onClick={() => setLogScale(v => !v)}
            sx={{
              fontWeight: 700, fontSize: '0.66rem', height: 26,
              bgcolor: logScale ? 'rgba(255,140,0,0.18)' : bloomberg.bg.tertiary,
              color: logScale ? '#ff8c00' : bloomberg.text.muted,
              border: `1px solid ${logScale ? '#ff8c00' : bloomberg.border.primary}`,
            }}
          />
        </MuiTooltip>

        <Box sx={{ width: 1, height: 22, bgcolor: bloomberg.border.primary, mx: 0.25 }} />

        {indicatorChips.map(({ key, label, color }) => (
          <Chip
            key={key} label={label} size="small" clickable onClick={() => toggle(key)}
            sx={{
              fontWeight: 600, fontSize: '0.66rem', height: 26,
              bgcolor: overlays[key] ? `${color}22` : bloomberg.bg.tertiary,
              color: overlays[key] ? color : bloomberg.text.muted,
              border: `1px solid ${overlays[key] ? color : bloomberg.border.primary}`,
            }}
          />
        ))}
      </Box>

      {/* Chart area with floating OHLC legend */}
      <Box sx={{ position: 'relative' }}>
        {live && (
          <Box sx={{
            position: 'absolute', top: 8, left: 12, zIndex: 4,
            display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center',
            px: 1.25, py: 0.5, borderRadius: 1,
            bgcolor: isDark ? 'rgba(10,14,23,0.78)' : 'rgba(255,255,255,0.82)',
            border: `1px solid ${bloomberg.border.primary}`,
            backdropFilter: 'blur(4px)', pointerEvents: 'none',
            fontFamily: 'IBM Plex Mono, monospace',
          }}>
            <Typography sx={{ fontSize: '0.7rem', color: bloomberg.text.muted }}>{live.date}</Typography>
            {[['O', live.o], ['H', live.h], ['L', live.l], ['C', live.c]].map(([k, v]) => (
              <Typography key={k as string} sx={{ fontSize: '0.72rem', color: bloomberg.text.secondary }}>
                <span style={{ color: bloomberg.text.muted }}>{k}</span>{' '}
                <span style={{ fontWeight: 600, color: bloomberg.text.primary }}>{fmt(v as number)}</span>
              </Typography>
            ))}
            <Typography sx={{ fontSize: '0.72rem' }}>
              <span style={{ color: bloomberg.text.muted }}>Vol</span>{' '}
              <span style={{ fontWeight: 600, color: bloomberg.text.secondary }}>{fmtVol(live.v)}</span>
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: chgColor }}>
              {live.chg != null ? `${live.chg >= 0 ? '+' : ''}${live.chg.toFixed(2)}%` : ''}
            </Typography>
          </Box>
        )}
        <div ref={mainRef} style={{ width: '100%' }} />
        {overlays.rsi && (
          <Box sx={{ position: 'relative', mt: 0.5 }}>
            <Typography sx={{ position: 'absolute', top: 2, left: 8, zIndex: 3, fontSize: '0.65rem', color: COLORS.rsi, fontWeight: 700, pointerEvents: 'none' }}>RSI (14)</Typography>
            <div ref={rsiRef} style={{ width: '100%' }} />
          </Box>
        )}
        {overlays.macd && (
          <Box sx={{ position: 'relative', mt: 0.5 }}>
            <Typography sx={{ position: 'absolute', top: 2, left: 8, zIndex: 3, fontSize: '0.65rem', color: COLORS.macd, fontWeight: 700, pointerEvents: 'none' }}>MACD (12,26,9)</Typography>
            <div ref={macdRef} style={{ width: '100%' }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProChart;
